import { useState, useCallback } from 'react';
import { trailService, TrailsListParams } from '../services/trailService';
import { SingleTrail } from '../../../Resources/Data/Trails';

export interface UseTrailsState {
  trails: SingleTrail[];
  totalTrails: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

export interface UseTrailsActions {
  fetchTrails: (params?: TrailsListParams) => Promise<void>;
  deleteTrail: (id: string | number) => Promise<boolean>;
  updateTrailStatus: (id: string | number, status: string) => Promise<boolean>;
  setCurrentPage: (page: number) => void;
  refreshTrails: () => Promise<void>;
}

export const useTrails = (initialParams: TrailsListParams = {}) => {
  const [state, setState] = useState<UseTrailsState>({
    trails: [],
    totalTrails: 0,
    currentPage: initialParams.page || 1,
    totalPages: 0,
    loading: false,
    error: null
  });

  const [filters, setFilters] = useState<TrailsListParams>(initialParams);

  const fetchTrails = useCallback(async (params?: TrailsListParams) => {
    const queryParams = params || filters;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await trailService.getTrails({
        ...queryParams,
        page: queryParams.page || state.currentPage
      });

      const { trails, count } = response.data;
      const pageSize = queryParams.pageSize || 12;
      const totalPages = Math.ceil(count / pageSize);

      setState(prev => ({
        ...prev,
        trails,
        totalTrails: count,
        totalPages,
        loading: false
      }));

      if (params) {
        setFilters(params);
      }
    } catch (error) {
      console.error('Error fetching trails:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch trails. Please try again.'
      }));
    }
  }, []);

  const deleteTrail = async (id: string | number): Promise<boolean> => {
    try {
      await trailService.deleteTrail(id);
      
      // Update local state
      setState(prev => ({
        ...prev,
        trails: prev.trails.filter(trail => trail.id !== Number(id)),
        totalTrails: prev.totalTrails - 1
      }));
      
      return true;
    } catch (error) {
      console.error('Failed to delete trail:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to delete trail. Please try again.'
      }));
      return false;
    }
  };

  const updateTrailStatus = async (id: string | number, status: string): Promise<boolean> => {
    try {
      let response;
      
      if (status === 'active') {
        response = await trailService.activateTrail(id);
      } else if (status === 'deleted') {
        response = await trailService.deactivateTrail(id);
      } else {
        response = await trailService.updateTrailStatus(id, status);
      }

      if (response.status === 200) {
        // Update local state
        setState(prev => ({
          ...prev,
          trails: prev.trails.map(trail => 
            trail.id === Number(id) 
              ? { ...trail, status: status as any }
              : trail
          )
        }));
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to update trail status:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to update trail status. Please try again.'
      }));
      return false;
    }
  };

  const setCurrentPage = (page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  };

  const refreshTrails = async () => {
    await fetchTrails(filters);
  };

  const actions: UseTrailsActions = {
    fetchTrails,
    deleteTrail,
    updateTrailStatus,
    setCurrentPage,
    refreshTrails
  };

  return {
    ...state,
    ...actions,
    filters
  };
}; 